import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionValue, StyleSheet, Text, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { getActivePlayers } from '../../../services/api';
import { deltaDNAService } from '../../../services/deltaDNA/deltadna.service';
import { getSocket } from '../../../services/socket';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { setActivePlayers } from '../../../store/invites/inviteSlice'; // Import the action
import { env } from '../../../utils/env';
import { gameHelixConfig } from '../../../utils/helpers/gameHelixConfig';
import { RootStackParamList } from '../../navigator';
import { isInsufficientGiftBalancePacket } from './giftAckUtils';
import { PAY_TO_PLAY_MIN_LEVEL } from './types';

let globalJustSwitchedTable = false;

const gameIframeStyle: React.CSSProperties = {
	position: 'absolute',
	top: 0,
	left: 0,
	right: 0,
	bottom: 0,
	width: '100%',
	height: '100%',
	border: 'none',
	backgroundColor: '#420304',
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'Game'>;
interface GameTableProps {
	tableId?: number | string;
	roomSlug?: string;
	isTutorial?: boolean;
	onGameEvent?: (event: string, data?: any) => void;
	isTournamentRoom?: boolean;
}

export const TutorialTable: React.FC<GameTableProps> = ({
	tableId,
	roomSlug,
	isTutorial = true,
	onGameEvent,
	isTournamentRoom = false,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const [gameStarted, setGameStarted] = useState(false);
	const [gameData, setGameData] = useState<any>(null);
	const [embedReady, setEmbedReady] = useState(false);
	console.log('tutoriaaaalllllll');
	deltaDNAService.tutorialCompletedEvent();
	// Get auth data from Redux store
	const auth = useSelector((state: RootState) => state.auth);
	const i18nBundle = useSelector((state: RootState) => state.i18n.bundle);
	const langCode = useSelector((state: RootState) => state.i18n.langCode);

	const isLogged = useSelector(selectIsLogged);
	const socket = useMemo(() => {
		if (!auth.user?.id || !auth.user?.username) {
			return undefined;
		}
		return getSocket(String(auth.user.id), String(auth.user.username));
	}, [auth.user?.id, auth.user?.username]);
	const navigation = useNavigation<NavigationProp>();

	useEffect(() => {
		if (!isLogged || !auth.user?.id || !auth.user?.username) {
			console.log('QuickSeat: Waiting for auth...');
			return;
		}
		const quickSeatSocket = getSocket(String(auth.user.id), String(auth.user.username));

		if (!quickSeatSocket) {
			console.log('No socket available for quickSeat listener');
			return;
		}

		const handleQuickSeat = (packet: any) => {
			console.log('Packet:', packet);
			console.log('Current tableId:', tableId);

			const data = packet?.data || packet || {};

			if (packet?.success && data.tableId && data.tableId !== tableId) {
				console.log(`SWITCHING from table ${tableId} to ${data.tableId}`);
				globalJustSwitchedTable = true;
				if (gameStarted) {
					try {
						(
							iframeRef.current?.contentWindow as { destroyGame?: () => void } | undefined
						)?.destroyGame?.();
						setGameStarted(false);
					} catch (error) {
						console.error('Failed to destroy game:', error);
					}
				}

				navigation.replace('Game', {
					tableId: data.tableId,
					roomSlug: data.roomSlug || roomSlug,
					isTournamentRoom: false,
				});
				setTimeout(() => {
					globalJustSwitchedTable = false;
				}, 2000);
			} else if (packet?.success && data.tableId === tableId) {
				console.log('ℹQuickSeat to same table, no navigation needed');
			}
		};

		quickSeatSocket.on('quickSeat', handleQuickSeat);
		console.log('QuickSeat listener registered successfully');

		return () => {
			quickSeatSocket.off('quickSeat', handleQuickSeat);
		};
	}, [isLogged, auth.user, tableId, gameStarted, navigation, roomSlug]);

	useEffect(() => {
		const handleHashChange = () => {
			if (globalJustSwitchedTable) {
				console.log('Ignoring hash change - just switched tables');
				window.history.replaceState(null, '', window.location.pathname);
				return;
			}

			const hash = window.location.hash;
			console.log('Hash changed tooo:', hash);

			const roomMatch = hash.match(/#\/room\/([^/]+)/);
			const lobbyMatch = hash === '#/lobby' || hash.startsWith('#/lobby');
			if (roomMatch && onGameEvent) {
				const hashRoomSlug = roomMatch[1];
				console.log('Detected room navigation to:', hashRoomSlug);

				window.history.replaceState(null, '', window.location.pathname);
				onGameEvent('goToLobby', { roomSlug: hashRoomSlug, roomId: hashRoomSlug });
			} else if (lobbyMatch && onGameEvent) {
				window.history.replaceState(null, '', window.location.pathname);
				onGameEvent('goToLobby');
			}
		};

		window.addEventListener('hashchange', handleHashChange);

		return () => {
			window.removeEventListener('hashchange', handleHashChange);
		};
	}, [onGameEvent]);

	// Check authentication and redirect to login if not logged in
	useEffect(() => {
		if (!isLogged) {
			console.log('🔐 User not logged in, redirecting to login...');
			navigation.navigate('Login' as never);
		}
	}, [isLogged, navigation]);

	useEffect(() => {
		const onMessage = (e: MessageEvent) => {
			if (e.origin !== window.location.origin) {
				return;
			}
			if (e.data?.type === 'SVARA_EMBED_READY') {
				setEmbedReady(true);
			}
			if (e.data?.type === 'SVARA_GAME_EVENT' && typeof e.data.name === 'string') {
				window.dispatchEvent(new CustomEvent(e.data.name, { detail: e.data.detail }));
			}
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, []);

	// Initialize game data
	useEffect(() => {
		console.log('🎮 GameTable auth effect triggered:', { auth, isLogged });

		if (!isLogged || !auth.token) {
			console.log('⏳ Waiting for authentication...');
			return;
		}

		// Get proper game dimensions like the Angular app
		const getGameDimensions = () => {
			let ratio = 1;
			const w = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
			const h = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

			if (
				// @ts-ignore
				window.screen.systemXDPI !== undefined &&
				// @ts-ignore
				window.screen.logicalXDPI !== undefined &&
				// @ts-ignore
				window.screen.systemXDPI > window.screen.logicalXDPI
			) {
				// @ts-ignore
				ratio = window.screen.systemXDPI / window.screen.logicalXDPI;
			} else if (window.devicePixelRatio !== undefined) {
				ratio = window.devicePixelRatio;
			}

			console.log('🎮 Game dimensions:', { width: w, height: h, ratio });
			return { width: w, height: h, ratio };
		};

		const dimensions = getGameDimensions();

		const gameInitData = {
			userId: auth.user?.numericId,
			tableId,
			width: dimensions.width * dimensions.ratio,
			height: dimensions.height * dimensions.ratio,
			ownerId: undefined,
			tutorial: isTutorial,
			isVipRoom: false,
			isTournamentRoom: isTournamentRoom,
			tableLevel: 1,
			playerName: auth.user?.username,
			// playerAvatarUrl: auth.user?.avatarFileId,
			// ||
			// "https://play.vipsvara.com/assets/avatar_man.png?v=63",
			// "https://play.vipsvara.com/assets/loading_screen_en.jpg?v=63",
			soundEnabled:
				auth.user?.isSoundEffectsEnabled !== undefined ? auth.user.isSoundEffectsEnabled : true,
			translations: i18nBundle || {},
			language: langCode || 'en',
			drawingBuffer: false,
			payToPlay: (auth.user?.level || 1) >= PAY_TO_PLAY_MIN_LEVEL,
			freeToPlay: false,
			payToPlayMinLevel: PAY_TO_PLAY_MIN_LEVEL,
			token: auth.token, // Use the actual token from auth
			api: {
				game: `${env('SVARA_API_URL')}/api/game` || 'https://play.vipsvara.com/api/game',
				user: `${env('SVARA_API_URL')}/api/game` || 'https://play.vipsvara.com/api/user',
			},
			eventRunning: '',
			isEventRunning: false,
			powerMeter: !!auth.user?.isHandPowerEnabled,
			helix: gameHelixConfig(),
		};

		console.log('🎮 Auth object:', auth);
		console.log('🎮 Auth token:', auth.token);
		console.log('🎮 Auth user:', auth.user);
		console.log('🎮 Game init data:', gameInitData);
		setGameData(gameInitData);
	}, [auth, isLogged, tableId, isTutorial, i18nBundle, isTournamentRoom, langCode]);

	// Handle game events from PIXI
	useEffect(() => {
		const handleGameEvents = (event: CustomEvent) => {
			console.log('eventtttt', event);
			console.log('Game event:', event.type, event.detail);
			console.log('event invite', event.type);
			switch (event.type) {
				case 'becomeVIP':
					onGameEvent?.('becomeVIP');
					break;
				case 'notEnoughChips':
					onGameEvent?.('notEnoughChips');
					break;
				case 'buyChips':
					onGameEvent?.('buyChips');
					break;
				case 'invite':
					const fetchAndShowInviteModal = async () => {
						try {
							const activePlayers = await getActivePlayers({
								start: 0,
								limit: 100,
								tableId: tableId,
							});
							console.log('🔍 Fetched active players count:', activePlayers.data.items.length);
							console.log('Active players fetched:', activePlayers);
							dispatch(setActivePlayers(activePlayers.data.items));
							onGameEvent?.('invite', activePlayers);
						} catch (error) {
							console.error('Failed to fetch active players:', error);
							onGameEvent?.('invite', []);
						}
					};
					fetchAndShowInviteModal();
					break;

				case 'setSound':
					onGameEvent?.('setSound', event.detail);
					break;
				case 'openNotifications':
					onGameEvent?.('openNotifications');
					break;
				case 'showBlackscreenAlert':
					onGameEvent?.('showBlackscreenAlert');
					break;
				case 'hideBlackscreenAlert':
					onGameEvent?.('hideBlackscreenAlert');
					break;
				case 'tableDoesNotExist':
					onGameEvent?.('tableDoesNotExist');
					break;
			}
		};

		const handleInviteReceived = (data: any) => {
			console.log('Received invite:', data);
			onGameEvent?.('inviteReceived', data);
		};

		const handleGiftSendAck = (packet: any) => {
			if (isInsufficientGiftBalancePacket(packet)) {
				onGameEvent?.('notEnoughChips');
			}
		};

		// Add event listeners
		const events = [
			'becomeVIP',
			'notEnoughChips',
			'buyChips',
			'invite',
			'setSound',
			'openNotifications',
			'showBlackscreenAlert',
			'hideBlackscreenAlert',
			'tableDoesNotExist',
		];

		events.forEach(eventType => {
			window.addEventListener(eventType as keyof WindowEventMap, handleGameEvents as EventListener);
		});

		if (socket) {
			socket.on('inviteUser', handleInviteReceived);
			socket.on('giftSendingAck', handleGiftSendAck);
			socket.on('winGiftSendingAck', handleGiftSendAck);
		}

		return () => {
			events.forEach(eventType => {
				window.removeEventListener(
					eventType as keyof WindowEventMap,
					handleGameEvents as EventListener,
				);
			});
			socket?.off('inviteUser', handleInviteReceived);
			socket?.off('giftSendingAck', handleGiftSendAck);
			socket?.off('winGiftSendingAck', handleGiftSendAck);
		};
	}, [onGameEvent, dispatch, socket, tableId]);

	useEffect(() => {
		console.log('🎮 TutorialTable useEffect triggered:', {
			gameData: !!gameData,
			embedReady,
			gameStarted,
		});

		if (!gameData) {
			console.log('⏳ Waiting for game data...');
			return;
		}

		if (!embedReady) {
			console.log('⏳ Waiting for /svaragame/embed.html...');
			return;
		}

		if (gameStarted) {
			console.log('⏳ Game already started');
			return;
		}

		const attemptStartGame = (): boolean => {
			const win = iframeRef.current?.contentWindow as
				| (Window & {
						startGame?: (data: unknown) => void;
						socket?: unknown;
						PIXI?: {
							app?: { controller?: { gotoLobby?: () => void; __tutorialLobbyPatched?: boolean } };
						};
				  })
				| undefined;
			if (!win?.startGame) {
				console.error('❌ iframe startGame is not available!');
				return false;
			}

			try {
				if (socket) {
					win.socket = socket;
					console.log('🔌 Socket made available to PIXI game (iframe)');
				}

				console.log('🎮 Starting PIXI game with data:', gameData);
				win.startGame(gameData);

				const controller = win.PIXI?.app?.controller;
				if (
					controller &&
					typeof controller.gotoLobby === 'function' &&
					!controller.__tutorialLobbyPatched
				) {
					controller.__tutorialLobbyPatched = true;
					controller.gotoLobby = () => {
						onGameEvent?.('goToLobby');
					};
				}

				setGameStarted(true);
				console.log('🎮 PIXI game started successfully');

				return true;
			} catch (error) {
				console.error('Failed to start game:', error);
				return false;
			}
		};

		if (attemptStartGame()) {
			return;
		}

		const retryDelays = [100, 200, 500, 1000];
		let retryCount = 0;

		const retryStartGame = () => {
			if (retryCount >= retryDelays.length) {
				console.error('❌ Failed to start game after all retries');
				return;
			}

			const delay = retryDelays[retryCount];
			console.log(`⏳ Retrying game start in ${delay}ms (attempt ${retryCount + 1})`);

			setTimeout(() => {
				if (attemptStartGame()) {
					return;
				}
				retryCount++;
				retryStartGame();
			}, delay);
		};

		retryStartGame();
	}, [gameData, embedReady, socket, gameStarted, onGameEvent]);

	useEffect(() => {
		const iframe = iframeRef.current;
		return () => {
			if (!gameStarted) {
				return;
			}
			const win = iframe?.contentWindow as (Window & { destroyGame?: () => void }) | undefined;
			if (win?.destroyGame) {
				try {
					win.destroyGame();
				} catch (error) {
					console.error('Failed to destroy game:', error);
				}
			}
		};
	}, [gameStarted]);

	return (
		<View style={styles.container}>
			{!embedReady && (
				<View style={styles.loadingContainer}>
					<Text style={styles.loadingText}>
						{t('T_SITE_GAME_LOADING', { defaultValue: 'Loading game...' })}
					</Text>
				</View>
			)}
			<iframe
				ref={iframeRef}
				src='/svaragame/embed.html'
				title='Svara tutorial'
				style={gameIframeStyle}
			/>
		</View>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		alignSelf: 'stretch',
		position: 'relative',
		width: '100%',
		minHeight: '100vh' as DimensionValue,
		height: '100vh' as DimensionValue,
	},
	loadingContainer: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: '#000',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 1000,
	},
	loadingText: {
		color: '#fff',
		fontSize: 18,
		fontWeight: 'bold',
	},
});

export default TutorialTable;
